export interface Volume {
  number: string
  title: string
  subtitle: string
  intro: string[]
  questions?: string[]
  highlight?: string
  afterHighlight?: string
  bulletsIntro: string
  bullets: string[]
  sequenceIntro?: string
  sequence?: string
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  resultLead?: string
  resultQuote?: string
  result: string
  resultEmphasis?: string
  popular?: boolean
  popularLabel?: string
}

export const volumes: Volume[] = [
  {
    number: 'Volume 01',
    title: 'Workplace Hazard Identification & Risk Assessment Atlas',
    subtitle: 'Visual Training for Hazard Recognition, Risk Assessment & Workplace Prevention',
    intro: [
      'Every effective workplace safety training program starts with recognizing potential hazards.',
      'Students need to understand what a hazard is, how exposure may occur, what consequences could result and which prevention approaches are relevant.',
      'But teaching these concepts through theory alone can make training unnecessarily difficult.',
      'This volume transforms workplace hazard recognition into professional visual teaching material.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Workplace hazard fundamentals',
      'Hazard versus risk',
      'Workplace hazard categories',
      'Mechanical hazards',
      'Electrical hazards',
      'Chemical hazards',
      'Physical hazards',
      'Ergonomic risks',
      'Environmental hazards',
      'Exposure pathways',
      'Potential consequences',
      'Likelihood and severity concepts',
      'Risk assessment examples',
      'Hierarchy of controls',
      'Workplace observation methods',
      'Factory hazard identification',
      'Warehouse hazard scenarios',
      'Construction workplace examples',
      'Before-and-after comparisons',
      'Correct-versus-incorrect conditions',
      'Find-the-hazards challenges',
      'Risk assessment case studies',
      'Student observation activities',
      'Instructor reference cards',
      'Knowledge assessments',
    ],
    result:
      'Give your students a clearer understanding of workplace hazards, risk assessment principles and preventive control concepts through detailed visual training resources.',
  },
  {
    number: 'Volume 02',
    title: 'Warehouse, Logistics & Material Handling Safety Atlas',
    subtitle: 'Visual Training for Warehouses, Industrial Traffic & Material Handling Hazards',
    intro: [
      'Warehouse safety involves much more than moving boxes.',
      'Forklifts, pedestrians, storage systems, loading docks and material handling equipment operate within dynamic environments where multiple hazards can overlap.',
      'Explaining these interactions requires clear visual examples.',
      'This volume provides professional training resources to help instructors teach warehouse hazard recognition and material handling awareness.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Warehouse safety fundamentals',
      'Warehouse layout diagrams',
      'Forklift awareness',
      'Industrial truck identification',
      'Pedestrian interaction',
      'Workplace traffic routes',
      'Vehicle blind spots',
      'Loading dock hazards',
      'Delivery areas',
      'Pallet storage',
      'Storage rack awareness',
      'Material stacking',
      'Falling-object hazards',
      'Powered material handling',
      'Manual handling',
      'Conveyor interactions',
      'Warehouse housekeeping',
      'Walkway obstructions',
      'Access routes',
      'Equipment condition awareness',
      'Loading-area scenarios',
      'Storage hazard comparisons',
      'Correct-versus-incorrect layouts',
      'Warehouse observation activities',
      'Instructor assessments',
    ],
    result:
      'Help students recognize warehouse hazards and understand material handling and workplace traffic risks through structured visual scenarios.',
  },
  {
    number: 'Volume 03',
    title: 'Machinery, Tools & Hazardous Energy Safety Atlas',
    subtitle: 'Visual Training for Mechanical Hazards, Industrial Machinery & Energy Awareness',
    intro: [
      'Industrial machinery contains moving parts, complex mechanical assemblies and multiple sources of hazardous energy.',
      'Students need to understand where those hazards originate and how exposure could occur.',
      'Long explanations aren’t always enough.',
      'This volume turns industrial machinery safety concepts into clear, professional technical illustrations and practical teaching activities.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Industrial machinery safety map',
      'Mechanical hazard classification',
      'Rotating shafts',
      'Industrial gears',
      'Mechanical transmissions',
      'Belt and pulley systems',
      'Conveyor belt anatomy',
      'Roller conveyor hazards',
      'Pinch points',
      'Crushing and shearing zones',
      'Industrial press machinery',
      'Machine guarding fundamentals',
      'Fixed and interlocked guards',
      'Presence-sensing concepts',
      'Emergency stop versus guarding',
      'Industrial drilling equipment',
      'Bench grinders',
      'Handheld industrial tools',
      'Industrial mixers',
      'Automated production lines',
      'Electrical energy',
      'Hydraulic energy',
      'Pneumatic systems',
      'Mechanical stored energy',
      'Gravity-related hazards',
      'Energy isolation awareness',
      'Lockout/tagout concepts',
      'Multiple energy sources',
      'Machine condition recognition',
      'Industrial hoses and fittings',
      'Worker-machine interaction zones',
      'Machinery guarding comparisons',
      'Factory hazard identification challenges',
      'Instructor technical assessments',
    ],
    result:
      'Make complex industrial machinery, mechanical hazards and hazardous energy concepts easier to visualize, explain and assess.',
  },
  {
    number: 'Volume 04',
    title: 'Fire, Electrical & Emergency Safety Atlas',
    subtitle: 'Visual Training for Fire Prevention, Electrical Hazards & Emergency Preparedness',
    intro: [
      'Fire, electrical and emergency hazards can occur in many workplace environments.',
      'Students need to recognize dangerous conditions, understand basic prevention concepts and learn the importance of appropriate emergency arrangements.',
      'This volume organizes essential awareness topics into clear visual training material.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Fire hazard fundamentals',
      'Ignition sources',
      'Combustible materials',
      'Fire prevention concepts',
      'Industrial fire scenarios',
      'Fire protection equipment awareness',
      'Emergency access considerations',
      'Workplace evacuation awareness',
      'Emergency route maps',
      'Emergency communication',
      'Electrical hazard fundamentals',
      'Electrical equipment recognition',
      'Damaged cable awareness',
      'Exposed electrical components',
      'Electrical cabinet hazards',
      'Electrical equipment near moisture',
      'Industrial electrical scenarios',
      'Warning signs',
      'Emergency preparedness situations',
      'Correct-versus-incorrect examples',
      'Workplace hazard challenges',
      'Classroom discussion activities',
      'Student knowledge checks',
      'Quick-reference training sheets',
    ],
    result:
      'Make workplace fire hazards, electrical dangers and emergency preparedness concepts easier to explain through practical visual references.',
  },
  {
    number: 'Volume 05',
    title: 'Falls, Ladders & Working at Height Safety Atlas',
    subtitle: 'Visual Training for Fall Hazards, Access Equipment & Elevated Workplaces',
    intro: [
      'Fall hazards are not limited to construction sites.',
      'They can appear in warehouses, factories, maintenance environments and commercial workplaces.',
      'Students need to understand how workplace conditions, access equipment and elevated work arrangements influence exposure.',
      'This volume helps instructors explain these risks through professional illustrations and visual comparisons.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'Fall hazard fundamentals',
      'Slips and trips',
      'Wet and uneven surfaces',
      'Walkway obstructions',
      'Openings and edges',
      'Ladder hazards',
      'Ladder components',
      'Ladder condition awareness',
      'Access equipment comparisons',
      'Elevated work environments',
      'Scaffold awareness',
      'Working platform concepts',
      'Guardrail awareness',
      'Fall protection concepts',
      'Falling-object hazards',
      'Elevated worksite layouts',
      'Ground conditions',
      'Access routes',
      'Industrial building scenarios',
      'Correct-versus-incorrect examples',
      'Hazard-identification exercises',
      'Student assessments',
      'Instructor field cards',
    ],
    result:
      'Help students recognize fall hazards and understand ladder, access and elevated-workplace safety concepts with greater visual clarity.',
  },
  {
    number: 'Volume 06',
    title: 'PPE, Chemical Hazards & Occupational Health Atlas',
    subtitle: 'Visual Training for Protective Equipment, Chemical Awareness & Workplace Health',
    intro: [
      'Some of the most important workplace hazards are not immediately obvious.',
      'Chemical exposure, noise, dust, ergonomic risks and other occupational health concerns require effective awareness training.',
      'This volume provides professional visual resources that help instructors explain protective equipment, hazard exposure and workplace health concepts.',
    ],
    bulletsIntro: 'Inside:',
    bullets: [
      'PPE fundamentals',
      'Head protection',
      'Eye and face protection',
      'Hearing protection',
      'Hand protection',
      'Protective footwear',
      'Respiratory protection awareness',
      'High-visibility clothing',
      'PPE limitations',
      'Equipment recognition activities',
      'Chemical hazard fundamentals',
      'Chemical container identification',
      'Hazard communication',
      'Safety data sheet awareness',
      'Exposure pathways',
      'Spill awareness',
      'Dust exposure',
      'Noise hazards',
      'Ventilation concepts',
      'Heat exposure',
      'Occupational health awareness',
      'Ergonomic risks',
      'Manual material handling',
      'Repetitive work',
      'Workstation arrangements',
      'Correct-versus-incorrect examples',
      'Exposure scenarios',
      'Instructor reference sheets',
    ],
    result:
      'Help students recognize personal protective equipment, chemical hazards and occupational health risks through organized visual training resources.',
  },
]
